<template>
  <nx-fullscreen-dialog :active="active" @dismiss="dismiss" fix-toolbar :title="$t('message.form.scopes.slides')">
    <template slot="toolbar-elements">
      <nx-scrollytelling-slide-editor-buttons :creating-new-slide="creatingNewSlide"
                                              :in-edit-view="inEditView"
                                              :in-wizard-view="inWizardView"
                                              :is-dirty="isDirty"
                                              :valid-form="validForm"
                                              :loading="loading"
                                              :slide-index="currentSlideIndex"
                                              @confirm="handleSlideFormConfirm"
                                              @delete="handleSlideFormDelete"
                                              @leave="handleSlideFormLeave"
                                              @undo="handleSlideFormUndo" />
    </template>
    <v-layout row wrap class="nx-scrollytelling-slide-editor">
      <v-flex v-if="!isEmpty" xs12 sm6 class="hidden-xs-only">
        <nx-scrollytelling-slide-editor-preview ref="preview" :preview-url="previewUrl"/>
      </v-flex>
      <v-flex v-else xs12 sm6 class="nx-scrollytelling-slide-editor__empty hidden-xs-only">
        <span class="text" v-text="$t('message.content.editor.scrollytelling.preview')"></span>
      </v-flex>
      <v-flex xs12 sm6 class="nx-scrollytelling-slide-editor__edit" :class="{ edit: inEditView }">
        <v-slide-x-reverse-transition hide-on-leave>
          <nx-scrollytelling-slide-list v-if="inListView"
                                        class="px-4"
                                        :value="value"
                                        @move="handleMove"
                                        @edit="editSlide"
                                        @create="setWizardView"/>
          <nx-scrollytelling-slide-editor-form v-if="inEditView"
                                               class="px-4"
                                               @form="updateForm"
                                               @input="updateCurrentSlide"
                                               :value="currentSlide"
                                               v-bind="currentSlide"/>
          <nx-scrollytelling-slide-editor-wizard v-if="inWizardView"
                                                 class="px-4"
                                                 :create-opening="isEmpty"
                                                 @create="createSlideFromTemplate"/>
        </v-slide-x-reverse-transition>
      </v-flex>
    </v-layout>
    <nx-dialog-confirm
      :text="$t('message.content.editor.scrollytelling.unsavedChangesWithErrorsConfirmText')"
      :title="$t('message.content.editor.scrollytelling.unsavedChangesWithErrors')"
      :accept-text="$t('message.content.editor.scrollytelling.unsavedChangesWithErrorsAcceptText')"
      :cancel-text="$t('message.content.editor.scrollytelling.unsavedChangesWithErrorsCancelText')"
      accept-color="primary"
      cancel-color="primary"
      ref="confirmLeaveBlock"
    />
    <nx-dialog-confirm
      :text="$t('message.content.editor.unsavedChangesConfirmText')"
      :title="$t('message.content.editor.unsavedChanges')"
      :accept-text="$t('message.content.editor.unsavedChangesAcceptText')"
      :cancel-text="$t('message.content.editor.unsavedChangesCancelText')"
      accept-color="primary"
      cancel-color="primary"
      ref="confirmCloseEditor"
    />
    <nx-dialog-confirm
      :title="$t('message.content.editor.scrollytelling.confirmDeleteTitle')"
      :text="$t('message.content.editor.scrollytelling.confirmDeleteText')"
      ref="confirmDelete"
    />
  </nx-fullscreen-dialog>
</template>

<script>
import NxFullscreenDialog from '@/components/dialogs/FullscreenDialog';
import NxScrollytellingSlideList from '@/modules/scrollytelling/components/ScrollytellingSlideList';
import NxScrollytellingSlideEditorForm from '@/modules/scrollytelling/components/form/ScrollytellingSlideEditorForm';
import NxScrollytellingSlideEditorWizard from '@/modules/scrollytelling/components/ScrollytellingSlideEditorWizard';
import cloneDeep from 'lodash/cloneDeep';
import uuid from 'uuid/v4';
import NxDialogConfirm from '@/components/dialogs/Confirm';
import NxScrollytellingSlideEditorPreview
  from '@/modules/scrollytelling/components/ScrollytellingSlideEditorPreview';
import isEqual from 'lodash/isEqual';
import NxScrollytellingSlideEditorButtons from '@/modules/scrollytelling/components/ScrollytellingSlideEditorButtons';

const LIST_VIEW = 'list';
const EDIT_VIEW = 'form';
const WIZARD_VIEW = 'wizard';

export default {
  name: 'nx-scrollytelling-slide-editor',
  components: {
    NxScrollytellingSlideEditorButtons,
    NxScrollytellingSlideEditorPreview,
    NxDialogConfirm,
    NxScrollytellingSlideEditorWizard,
    NxScrollytellingSlideEditorForm,
    NxScrollytellingSlideList,
    NxFullscreenDialog,
  },
  props: {
    active: {
      type: Boolean,
    },
    loading: {
      type: Boolean,
    },
    value: {
      type: Array,
      required: true,
    },
    previewUrl: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      validForm: true,
      currentSlide: null,
      currentSlideIndex: null,
      originalCurrentSlideState: null,
      currentView: LIST_VIEW,
    };
  },
  computed: {
    creatingNewSlide() {
      return this.originalCurrentSlideState === null;
    },
    hasErrors() {
      return false;
    },
    isEmpty() {
      return this.value.length === 0;
    },
    isDirty() {
      return !isEqual(this.currentSlide, this.originalCurrentSlideState);
    },
    inListView() {
      return this.currentView === LIST_VIEW;
    },
    inEditView() {
      return this.currentView === EDIT_VIEW;
    },
    inWizardView() {
      return this.currentView === WIZARD_VIEW;
    },
  },
  methods: {
    async handleSlideFormConfirm() {
      if (this.loading) {
        return;
      }
      if (!this.validForm) {
        const agree = await this.$refs.confirmLeaveBlock.open();
        if (agree) {
          this.setListView();
        }
        return;
      }
      await this.commitCurrentSlide();
    },
    async handleSlideFormDelete() {
      const agree = await this.$refs.confirmDelete.open();
      if (!agree) {
        return;
      }
      this.deleteCurrentSlide();
      this.setListView();
    },
    async handleUnsavedChanges() {
      // Unsaved changes
      const agree = await this.$refs.confirmCloseEditor.open();
      if (agree) {
        this.$emit('dismiss');
        this.$nextTick(this.setListView);
      }
    },
    handleSlideFormUndo() {
      this.setCurrentSlide(this.originalCurrentSlideState);
    },
    async handleSlideFormLeave() {
      this.setListView();
    },
    createSlideFromTemplate({
      type,
      defaultFields = {},
    }) {
      const slide = {
        uuid: uuid(),
        type,
        ...defaultFields,
      };
      this.setCurrentSlide(slide);
      this.originalCurrentSlideState = null;
      this.setEditView();
    },
    async editSlide({ slide, index = null }) {
      await this.$store.dispatch('scrollytelling/SET_SLIDE_ID_BEING_EDITED', slide.uuid);
      this.setCurrentSlide(slide);
      this.currentSlideIndex = index;
      this.setEditView();
    },
    setCurrentSlide(slide) {
      this.currentSlide = cloneDeep(slide);
      this.originalCurrentSlideState = cloneDeep(slide);
    },
    setListView() {
      this.setCurrentSlide(null);
      this.currentSlideIndex = null;
      this.currentView = LIST_VIEW;
    },
    setEditView() {
      this.currentView = EDIT_VIEW;
    },
    setWizardView() {
      this.currentView = WIZARD_VIEW;
    },
    updateForm({ valid }) {
      this.validForm = valid;
    },
    async dismiss() {
      if (this.inEditView && this.isDirty) {
        await this.handleUnsavedChanges();
        return;
      }
      await this.$store.dispatch('scrollytelling/INCREMENT_DEFAULT_PREVIEW_KEY');
      this.$emit('dismiss');
      this.$nextTick(this.setListView);
    },
    async commitCurrentSlide() {
      const slides = cloneDeep(this.value);
      const slide = slides.find(element => this.currentSlide.uuid === element.uuid);
      if (!slide) {
        slides.push(this.currentSlide);
        this.setCurrentSlide(this.currentSlide);
      } else {
        Object.assign(slide, this.currentSlide);
        this.setCurrentSlide(slide);
      }
      this.$emit('input', slides);
    },
    deleteCurrentSlide() {
      const slides = this.value.filter(element => this.currentSlide.uuid !== element.uuid);
      this.$emit('input', slides);
    },
    handleMove(slides) {
      this.$emit('input', slides);
    },
    updateCurrentSlide(field, value) {
      this.$set(this.currentSlide, field, value);
    },
  },
};
</script>

<style lang="stylus" scoped>
@import '../../../assets/stylus/variables.styl'

.nx-scrollytelling-slide-editor {
  height: calc(100vh - 64px)
  overflow-y: hidden

  &__empty {
    background-color: #e9e9f2;
    display: flex;
    align-items: center;
    justify-content: center;

    .text {
      font-size: 14px;
      font-weight: 400;
      color: $nx-primary--darken-1;
    }
  }

  &__edit {
    padding: 16px 16px 6px 16px;
    max-height: calc(100vh - 64px)
    overflow-y: auto
    background-color: $nx-primary--lighten-5
  }
}
</style>
